'use client';

import aa from 'search-insights';
import { hasAnalyticsConsent } from '@/lib/analytics/consent';

// EWM-Algolia Insights PR-A (foundation).
//
// Singleton init for `search-insights` + thin helpers that PR-B will
// wire into autocomplete / search / PLP click + view + conversion
// events. This file is intentionally event-firing-free for v1 — it just
// makes sure `aa(...)` is initialised with the right tokens before any
// caller asks for a queryID-bound event.
//
// Cookie management: WE manage `_at_token` at the edge (see
// `src/middleware.ts`), so `search-insights` is initialised with
// `useCookie: false` to disable its internal `_ALGOLIA` cookie path.
// The userToken is then bound explicitly via `aa('setUserToken', ...)`.

const TOKEN_COOKIE = '_at_token';

let initialized = false;
// EWM-253 — the per-dealer secured key, supplied by the provider via
// `initInsights(securedKey)`. Cached so the event helpers can call the
// idempotent `initInsights()` (no-arg) without re-threading the key.
let securedApiKey: string | undefined;

// EWM-976 — a one-shot "promote userToken to the authenticated id" that is
// applied AFTER the first event fires post sign-in. Holds the authenticated
// token between `setAuthenticatedUser()` and the next event so that single
// event still carries the anonymous `userToken` alongside the new
// `authenticatedUserToken` (Algolia's documented stitch), and every event
// after it carries the authenticated token in BOTH fields.
let pendingUserTokenPromotion: string | undefined;

// SSR-safe cookie read. Returns undefined on the server (no document).
const readTokenFromCookie = (): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${TOKEN_COOKIE}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : undefined;
};

// EWM-976 — run immediately after an event is sent. Promotes `userToken` to
// the authenticated id exactly once, so the stitch event (anon + auth) has
// already been sent and all subsequent events carry auth in both fields.
const promoteUserTokenAfterEvent = (): void => {
  if (!pendingUserTokenPromotion) return;
  aa('setUserToken', pendingUserTokenPromotion);
  pendingUserTokenPromotion = undefined;
};

/**
 * Lazy-init Algolia Insights. Idempotent — safe to call from any code
 * path that fires an event, so callers don't have to coordinate with
 * the provider's mount.
 */
export const initInsights = (apiKey?: string): void => {
  // First call from the provider passes the secured key; cache it so the
  // event helpers (which call `initInsights()` with no arg) can init too.
  if (apiKey) securedApiKey = apiKey;
  if (initialized) return;
  if (typeof window === 'undefined') return;

  const appId = process.env.NEXT_PUBLIC_ALGOLIA_APP_ID;
  if (!appId || !securedApiKey) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[algolia-insights] missing app id or secured key; events will no-op until the key is minted');
    }
    return;
  }

  aa('init', {
    appId,
    apiKey: securedApiKey,
    useCookie: false,
    partial: true,
  });

  const userToken = readTokenFromCookie();
  if (userToken) aa('setUserToken', userToken);

  initialized = true;
};

/**
 * Stitch the anonymous session to an authenticated user, then promote the
 * authenticated id to `userToken` so signed-in events carry it in BOTH
 * `userToken` and `authenticatedUserToken` (EWM-976 finding 1 — Algolia
 * Personalisation needs the authenticated token as the userToken once known).
 *
 * `accountId` is the commercetools customer id, which is stable for the same
 * customer across sessions and devices, so it is a durable authenticated
 * token (no per-session hashing required — see EWM-976).
 *
 * Stitch mechanism: we set `authenticatedUserToken` now but defer switching
 * `userToken` to it until AFTER the next event fires (see
 * `promoteUserTokenAfterEvent`). That first post-login event therefore carries
 * the anonymous `userToken` alongside the new `authenticatedUserToken` — the
 * co-occurrence Algolia uses to link pre-login activity to the signed-in
 * identity. Every event after it carries the authenticated token in both fields.
 *
 * Idempotent — `useInsights` calls this once per logged-in account. Do NOT
 * reset the anon `userToken` on logout: Algolia's documented pattern is
 * "stitched journey survives". Re-setting the anon would break the trail.
 */
export const setAuthenticatedUser = (accountId: string | undefined): void => {
  // EWM-243a: don't bind the dealer's account id to the Insights session
  // without consent — identity linkage is tracking even before an event
  // fires.
  if (!hasAnalyticsConsent()) return;
  initInsights();
  if (!accountId || !initialized) return;
  aa('setAuthenticatedUserToken', accountId);
  const anonToken = readTokenFromCookie();
  if (anonToken && anonToken !== accountId) {
    // Defer the userToken switch so the next event stitches anon → auth.
    pendingUserTokenPromotion = accountId;
  } else {
    // No distinct anonymous token to stitch — promote immediately.
    aa('setUserToken', accountId);
  }
};

/**
 * Read the current anonymous `userToken` (`_at_token` cookie value).
 * Used by `useAlgoliaProducts` to attach `userToken` to the Algolia
 * search request so the response queryID can be linked back to events
 * fired from this user's session.
 */
export const getUserToken = (): string | undefined => readTokenFromCookie();

// ---------- Event helpers (called by PR-B) ----------

interface TrackClickArgs {
  index: string;
  queryID: string;
  objectIDs: string[];
  /**
   * 1-indexed positions of the clicked hits within the search result
   * set. Algolia's `clickedObjectIDsAfterSearch` requires this — used
   * for click-position bias analytics. Callers compute as
   * `page * hitsPerPage + indexInPage + 1`.
   */
  positions: number[];
  eventName: string;
}

/**
 * Fire a `clickedObjectIDsAfterSearch` event. Bind to tile click
 * handlers on autocomplete / search-results / PLP. `eventName` should
 * vary per surface (e.g. `"Hit clicked (PLP)"`) so the analytics
 * dashboard can break CTR down by entry point.
 */
export const trackClick = ({ index, queryID, objectIDs, positions, eventName }: TrackClickArgs): void => {
  if (!hasAnalyticsConsent()) return;
  initInsights();
  if (!initialized) return;
  aa('clickedObjectIDsAfterSearch', {
    eventName,
    index,
    queryID,
    objectIDs,
    positions,
  });
  promoteUserTokenAfterEvent();
};

interface TrackClickedFiltersArgs {
  index: string;
  /**
   * Applied refinements as Algolia `facet:value` strings (e.g.
   * `brand:NEXTECH`). Boolean/toggle facets use `attribute:true`.
   */
  filters: string[];
  eventName: string;
}

/**
 * Fire a `clickedFilters` event (EWM-977 finding 2). Bind to facet
 * interactions on EVERY faceted surface — PLP, search results and Algolia
 * Collections — so Algolia Personalisation learns facet affinity. Unlike the
 * GA4 `search_refined` event this is NOT gated to the /search route.
 */
export const trackClickedFilters = ({ index, filters, eventName }: TrackClickedFiltersArgs): boolean => {
  if (!hasAnalyticsConsent()) return false;
  initInsights();
  if (!initialized || filters.length === 0) return false;
  aa('clickedFilters', { eventName, index, filters });
  promoteUserTokenAfterEvent();
  return true;
};

interface TrackViewArgs {
  index: string;
  objectIDs: string[];
  eventName: string;
}

/**
 * Fire a `viewedObjectIDs` impression event. Bind to an IntersectionObserver
 * on tile rows so it fires once a tile has been in-view for ~1s.
 *
 * Returns whether the event was actually sent — `false` when consent is
 * declined or Insights isn't initialised yet (no secured key cached). Callers
 * that dedupe (once-per-view / once-per-order) MUST only record the "sent"
 * state on a `true` return, so a cold-start no-op can retry rather than being
 * permanently suppressed.
 */
export const trackView = ({ index, objectIDs, eventName }: TrackViewArgs): boolean => {
  if (!hasAnalyticsConsent()) return false;
  initInsights();
  if (!initialized || objectIDs.length === 0) return false;
  aa('viewedObjectIDs', {
    eventName,
    index,
    objectIDs,
  });
  promoteUserTokenAfterEvent();
  return true;
};

interface TrackConvertArgs {
  index: string;
  objectIDs: string[];
  eventName: string;
  /**
   * Present when the conversion is attributable to a search (queryID recalled
   * from the click store) → `addedToCartObjectIDsAfterSearch`. Absent for a
   * browse/direct conversion → `addedToCartObjectIDs`.
   */
  queryID?: string;
  /**
   * EWM-980 — per-object revenue payload (price + quantity). Price is the
   * customer's actual resolved cart unit price — NET / ex-GST: Electus stores
   * trade prices GST-exclusive (`includedInPrice=false`, GST charged on top),
   * so the CT line `price.value` the BFF passes through is already ex-tax. This
   * is the net (ex-tax) price EWM-980's AC asks for, and it matches the EWM-809
   * purchase-event basis (also `price.value`/`totalPrice`, i.e. net). NOT the
   * RRP/list price from the search record. Omitted when the price isn't available.
   */
  objectData?: PurchaseObjectData[];
  /** EWM-980 — ISO currency for the objectData prices (e.g. `AUD`). */
  currency?: string;
}

/**
 * Fire an add-to-cart event. Search-attributed when `queryID` is present.
 * Bind to add-to-cart callbacks on each surface so Algolia can compute CR +
 * revenue attribution per query.
 *
 * EWM-979 — uses `addedToCartObjectIDs[AfterSearch]`, which stamps
 * `eventSubtype: addToCart` on the event automatically (the previous
 * `convertedObjectIDs*` methods sent a bare `conversion` with no subtype).
 * EWM-980 — forwards `objectData` (price + quantity) and top-level `currency`
 * when supplied.
 */
export const trackConvert = ({
  index,
  objectIDs,
  eventName,
  queryID,
  objectData,
  currency,
}: TrackConvertArgs): boolean => {
  if (!hasAnalyticsConsent()) return false;
  initInsights();
  if (!initialized) return false;
  const revenue = {
    ...(objectData ? { objectData } : {}),
    ...(currency ? { currency } : {}),
  };
  if (queryID) {
    aa('addedToCartObjectIDsAfterSearch', { eventName, index, queryID, objectIDs, ...revenue });
  } else {
    aa('addedToCartObjectIDs', { eventName, index, objectIDs, ...revenue });
  }
  promoteUserTokenAfterEvent();
  return true;
};

// ---------- Purchase events (EWM-809) ----------

export interface PurchaseObjectData {
  price: number;
  quantity: number;
  discount?: number;
}

interface TrackPurchaseArgs {
  index: string;
  objectIDs: string[];
  objectData: PurchaseObjectData[];
  value: number;
  currency: string;
  eventName: string;
}

/**
 * Fire a non-search `purchasedObjectIDs` event with revenue payload. Used at
 * order confirmation for lines that carry no originating queryID.
 */
export const trackPurchase = ({
  index,
  objectIDs,
  objectData,
  value,
  currency,
  eventName,
}: TrackPurchaseArgs): boolean => {
  if (!hasAnalyticsConsent()) return false;
  initInsights();
  if (!initialized || objectIDs.length === 0) return false;
  aa('purchasedObjectIDs', { eventName, index, objectIDs, objectData, value, currency });
  promoteUserTokenAfterEvent();
  return true;
};

interface TrackPurchaseAfterSearchArgs extends TrackPurchaseArgs {
  queryID: string;
}

/**
 * Fire a search-attributed `purchasedObjectIDsAfterSearch` event with revenue
 * payload. Used at order confirmation for lines grouped by originating queryID.
 */
export const trackPurchaseAfterSearch = ({
  index,
  queryID,
  objectIDs,
  objectData,
  value,
  currency,
  eventName,
}: TrackPurchaseAfterSearchArgs): boolean => {
  if (!hasAnalyticsConsent()) return false;
  initInsights();
  if (!initialized || objectIDs.length === 0) return false;
  aa('purchasedObjectIDsAfterSearch', { eventName, index, queryID, objectIDs, objectData, value, currency });
  promoteUserTokenAfterEvent();
  return true;
};
